import React, { useCallback, useEffect } from 'react';
import { ROUTES, ROUTE_PATTERNS } from '../../util/routes';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginForm from '../login/LoginForm';
import ForgotPasswordForm from '../login/ForgotPasswordForm';
import ResetPasswordForm from '../login/ResetPasswordForm';
import Home from '../pages/Home';
import UpcomingEvents from '../pages/UpcomingEvents';
import Event from '../pages/Event';
import Players from '../pages/Players';
import PlayersList from '../pages/PlayersList';
import ProtectedRoute from '../router/ProtectedRoute';
import Prices from '../pages/Prices';
import { useAuth } from '../context/AuthContext';
import api from './API';
import SuccessBar from '../successbar/SuccessBar';
import Mail from '../pages/Mail';
import PlayerMail from '../pages/PlayerMail';
import WikiPage from '../pages/WikiPage';
import Textblockeditor from '../pages/Textblockeditor';
import Registrations from '../pages/Registrations';
import Statistics from '../pages/Statistics';
import RoundMail from '../pages/RoundMail';
import EventAnnouncement from '../pages/EventAnnouncement';
import MailQueue from '../pages/MailQueue';
import Locations from '../pages/Locations';
import ParticipantGroups from '../pages/ParticipantGroups';
import ProcessTemplates from '../pages/ProcessTemplates';
import FlatRates from '../pages/FlatRates';
import DocsPage from '../docs/DocsPage';
import orgaDoc from '../docs/orgaDoc';
import adminDoc from '../docs/adminDoc';
import AppShell from '../shell/AppShell';

// The three fixed wiki pages before dynamic wikis (/wiki/:id) — old bookmarks land on the start page,
// where the wiki tiles are.
const LEGACY_WIKI_PATHS = ['/rules', '/gamemodes', '/airsoftbomb'];

function Wrapper() {
  const { setAuth } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const initializeApiInterceptors = useCallback(() => {
    api.interceptors.response.use(
      (response) => {
        if (response) {
          setAuth(true);
          return response;
        }
      },
      (error) => {
        if (error.response?.status === 401) {
          setAuth(false);
        } else if (error.response?.status === 403) {
          error.response.data = 'Dazu bist du nicht berechtigt.';
        }
        return Promise.reject(error);
      }
    );
  }, [setAuth]);

  useEffect(() => {
    initializeApiInterceptors();
    api.get('/Login/CheckAuthentication').catch(() => {});
  }, [setAuth, initializeApiInterceptors]);

  return (
    <React.Fragment>
      <Routes>
        {/* Public routes */}
        <Route path={ROUTES.LOGIN} element={<LoginForm />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordForm />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordForm />} />

        {/* Protected routes — AppShell provides sidebar + bottom nav */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route path={ROUTES.HOME} element={<Home />} />
            <Route path={ROUTES.UPCOMING_EVENTS} element={<UpcomingEvents />} />
            <Route path={ROUTE_PATTERNS.EVENT} element={<Event />} />
            <Route path={ROUTES.USERS} element={<ProtectedRoute adminOnly><PlayersList /></ProtectedRoute>} />
            <Route path={ROUTE_PATTERNS.USER} element={<ProtectedRoute adminOnly><Players /></ProtectedRoute>} />
            <Route path={ROUTES.PRICES} element={<Prices />} />
            <Route path={ROUTE_PATTERNS.WIKI} element={<WikiPage />} />
            {LEGACY_WIKI_PATHS.map(path => (
              <Route key={path} path={path} element={<Navigate to={ROUTES.HOME} replace />} />
            ))}
            <Route path={ROUTES.REGISTRATIONS} element={<ProtectedRoute adminOnly><Registrations /></ProtectedRoute>} />
            <Route path={ROUTES.STATISTICS} element={<Statistics />} />
            <Route path={ROUTE_PATTERNS.MAIL_PLAYER} element={<ProtectedRoute adminOnly><PlayerMail /></ProtectedRoute>} />
            <Route path={ROUTE_PATTERNS.MAIL} element={<ProtectedRoute adminOnly><Mail /></ProtectedRoute>} />
            <Route path={ROUTE_PATTERNS.ROUNDMAIL} element={<ProtectedRoute adminOnly><RoundMail /></ProtectedRoute>} />
            <Route path={ROUTES.MAIL_QUEUE} element={<ProtectedRoute adminOnly><MailQueue /></ProtectedRoute>} />
            <Route path={ROUTES.TEXTBLOCK_EDITOR} element={<ProtectedRoute adminOnly><Textblockeditor /></ProtectedRoute>} />
            <Route path={ROUTES.EVENT_ANNOUNCEMENT} element={<ProtectedRoute adminOnly><EventAnnouncement /></ProtectedRoute>} />
            <Route path={ROUTES.LOCATIONS} element={<ProtectedRoute adminOnly><Locations /></ProtectedRoute>} />
            <Route path={ROUTES.PARTICIPANT_GROUPS} element={<ProtectedRoute adminOnly><ParticipantGroups /></ProtectedRoute>} />
            <Route path={ROUTES.PROCESS_TEMPLATES} element={<ProtectedRoute adminOnly><ProcessTemplates /></ProtectedRoute>} />
            <Route path={ROUTES.FLAT_RATES} element={<ProtectedRoute adminOnly><FlatRates /></ProtectedRoute>} />
            <Route path={ROUTES.DOCS_ORGA} element={<DocsPage doc={orgaDoc} />} />
            <Route path={ROUTES.DOCS_ADMIN} element={<ProtectedRoute adminOnly><DocsPage doc={adminDoc} /></ProtectedRoute>} />
          </Route>
        </Route>
      </Routes>
      <SuccessBar />
    </React.Fragment>
  );
}

export default Wrapper;
